import React from 'react';
import type { JSONContent } from '@tiptap/react';
import { Loader2, Paperclip, Send, FileText, Download, X, MessageSquare, Check, CheckCheck, MessageCircleOff } from 'lucide-react';
import { Button } from '../ui/Button';
import type { ChatMessageEditorHandle } from '../ui/ChatMessageEditor';

// Tiptap chargé à la demande uniquement (jamais au chargement du reste de l'app) — même principe que
// Notes/RichEditor.tsx, voir son commentaire d'origine.
const ChatMessageEditor = React.lazy(() => import('../ui/ChatMessageEditor').then((m) => ({ default: m.ChatMessageEditor })));
const RichMessagePreview = React.lazy(() => import('../ui/RichMessagePreview').then((m) => ({ default: m.RichMessagePreview })));

export interface ThreadMessage {
    id: string;
    author_type: 'admin' | 'employe';
    author_name: string;
    message: string;
    message_rich?: string | null;
    created_at: string;
    attachment_name?: string | null;
}

export interface ThreadRead { reader_id: string; last_read_at: string; }

const ATTACHMENT_ACCEPT = 'image/png,image/jpeg,image/gif,application/pdf';

// Panneau de droite d'une messagerie façon boîte de réception (liste à gauche, conversation ouverte à
// droite) — extrait de l'ancienne modale de fil pour vivre dans une mise en page à deux colonnes fixe
// plutôt qu'en popup, demande explicite d'Emeni (2026-08-19) après avoir vu la première version en modale.
// Reste partagé entre TeamMessagingSettings.tsx (établissement) et EmployeeMessages.tsx (salarié).
export function ConversationPanel({
    title, subtitle, messages, loading, myAuthorType,
    onReply, sending, onViewAttachment, viewingAttachmentId,
    otherReads = [], recipientCount = 1, allowReplies = true, onToggleAllowReplies,
}: {
    title: string; subtitle?: string;
    messages: ThreadMessage[]; loading: boolean; myAuthorType: 'admin' | 'employe';
    onReply: (payload: { text: string; rich: JSONContent; attachment: File | null }) => Promise<void>;
    sending: boolean;
    onViewAttachment: (messageId: string, win: Window | null) => void;
    viewingAttachmentId: string | null;
    // Mentions "Lu" des deux côtés (demande d'Emeni, 2026-08-19) : dates de lecture de l'AUTRE côté
    // uniquement — un message affiche "Lu" une fois sa date d'envoi dépassée par l'une de ces lectures.
    otherReads?: ThreadRead[];
    recipientCount?: number;
    // "Permettre le dialogue" : si désactivé, le salarié ne peut plus répondre (composeur masqué côté
    // salarié) ; l'établissement peut toujours basculer le réglage depuis l'en-tête de la conversation.
    allowReplies?: boolean;
    onToggleAllowReplies?: (next: boolean) => void;
}) {
    const editorRef = React.useRef<ChatMessageEditorHandle>(null);
    const [attachment, setAttachment] = React.useState<File | null>(null);
    const bottomRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        bottomRef.current?.scrollIntoView({ block: 'end' });
    }, [messages.length]);

    const submit = async (content?: { json: JSONContent; text: string }) => {
        const { json, text } = content || editorRef.current?.getContent() || { json: {} as JSONContent, text: '' };
        if (!text.trim() && !attachment) return;
        await onReply({ text: text.trim(), rich: json, attachment });
        editorRef.current?.clear();
        setAttachment(null);
    };

    return (
        <div className="flex flex-col h-full bg-surface">
            <div className="px-5 py-4 border-b border-border shrink-0">
                <div className="min-w-0">
                    <h2 className="text-sm font-bold text-content truncate">{title}</h2>
                    {subtitle && <p className="text-[11px] text-content-muted mt-0.5">{subtitle}</p>}
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
                {loading ? (
                    <div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                ) : (
                    <React.Suspense fallback={<div className="flex justify-center py-8"><Loader2 className="animate-spin text-content-muted" size={20} /></div>}>
                        {messages.map((m) => {
                            const isMine = m.author_type === myAuthorType;
                            const readsByThisMessage = isMine ? otherReads.filter((r) => r.last_read_at >= m.created_at) : [];
                            const readCount = readsByThisMessage.length;
                            const firstReadAt = readCount > 0 ? new Date(readsByThisMessage[0].last_read_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
                            return (
                                <div key={m.id} className={`max-w-[70%] p-3 rounded-2xl ${isMine ? 'bg-brand-600/10 ml-auto' : 'bg-surface-soft'}`}>
                                    <p className="text-[10px] font-bold text-content-muted mb-0.5">{m.author_name}</p>
                                    {(m.message || m.message_rich) && (
                                        <RichMessagePreview messageRich={m.message_rich} plainText={m.message} className={`text-content ${isMine ? 'text-right' : ''}`} />
                                    )}
                                    {m.attachment_name && (
                                        <button onClick={() => onViewAttachment(m.id, window.open('', '_blank'))} disabled={viewingAttachmentId === m.id}
                                            className={`flex items-center gap-1.5 mt-1.5 px-2.5 py-1.5 rounded-lg bg-surface border border-border text-xs text-content hover:border-brand-500 transition-all disabled:opacity-50 ${isMine ? 'ml-auto' : ''}`}>
                                            <FileText size={13} className="shrink-0" />
                                            <span className="truncate max-w-[160px]">{m.attachment_name}</span>
                                            <Download size={12} className="shrink-0 text-content-muted" />
                                        </button>
                                    )}
                                    <p className={`flex items-center gap-1 text-[9px] text-content-muted mt-1 ${isMine ? 'justify-end' : ''}`}>
                                        {new Date(m.created_at).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                        {isMine && readCount > 0 && (
                                            <span className="inline-flex items-center gap-0.5 text-brand-600 dark:text-brand-400 font-semibold">
                                                <CheckCheck size={11} /> lu à {firstReadAt}
                                            </span>
                                        )}
                                        {isMine && readCount === 0 && <Check size={11} className="text-content-muted" />}
                                    </p>
                                </div>
                            );
                        })}
                    </React.Suspense>
                )}
                <div ref={bottomRef} />
            </div>
            {myAuthorType === 'employe' && !allowReplies ? (
                <div className="p-4 border-t border-border shrink-0 flex items-center gap-2 text-xs text-content-muted">
                    <MessageCircleOff size={14} className="shrink-0" /> Cette conversation ne permet pas de répondre.
                </div>
            ) : (
                <div className="p-4 border-t border-border shrink-0 space-y-2">
                    {attachment && (
                        <div className="flex items-center gap-2 bg-surface-soft border border-border rounded-xl px-3 py-1.5">
                            <FileText size={13} className="text-content-muted shrink-0" />
                            <span className="text-xs text-content truncate flex-1">{attachment.name}</span>
                            <button onClick={() => setAttachment(null)} className="p-1 text-content-muted hover:text-content shrink-0"><X size={13} /></button>
                        </div>
                    )}
                    <div className="flex items-end gap-2">
                        <label className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft cursor-pointer transition-all shrink-0">
                            <Paperclip size={16} />
                            <input type="file" accept={ATTACHMENT_ACCEPT} className="hidden" onChange={(e) => setAttachment(e.target.files?.[0] || null)} />
                        </label>
                        <div className="flex-1">
                            <React.Suspense fallback={<div className="h-[72px] rounded-xl border border-border bg-surface-soft animate-pulse" />}>
                                <ChatMessageEditor ref={editorRef} disabled={sending} placeholder="Répondre…" />
                            </React.Suspense>
                        </div>
                        <Button onClick={() => submit()} disabled={sending} className="shrink-0 h-9 w-9 !p-0">
                            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export function EmptyConversationPanel({ label }: { label: string }) {
    return (
        <div className="flex flex-col items-center justify-center h-full text-center px-8 bg-surface">
            <MessageSquare size={32} className="text-content-muted opacity-30 mb-3" />
            <p className="text-sm text-content-muted">{label}</p>
        </div>
    );
}
